/**
 * /erp/settings — ERP-specific settings shell (Manager+ via /erp gate).
 *
 * Mirrors the CRM /settings shell pattern: a sidebar with sub-page
 * links + an Outlet for the active page. v1 PR-A entry: Warehouses.
 * Drivers + Transport land in PR-E; inventory adjustment reasons in
 * PR-B if a picklist surface is needed.
 */

import { Link, Outlet, createFileRoute } from "@tanstack/react-router";
import { Settings as SettingsIcon, Truck, User as UserIcon, Warehouse } from "lucide-react";
import type { ComponentType, SVGProps } from "react";

interface ErpSettingsNavEntry {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}

const NAV_ENTRIES: ErpSettingsNavEntry[] = [
  { to: "/erp/settings/warehouses", label: "Warehouses", icon: Warehouse },
  { to: "/erp/settings/drivers", label: "Drivers", icon: UserIcon },
  { to: "/erp/settings/transports", label: "Transport", icon: Truck },
];

export const Route = createFileRoute("/_app/erp/settings")({
  component: ErpSettingsLayout,
});

function ErpSettingsLayout() {
  return (
    <div className="page-shell space-y-5 p-4 sm:p-6">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <SettingsIcon className="size-5 opacity-70" aria-hidden />
          ERP Settings
        </h1>
        <p className="text-sm opacity-60">
          Configure the people, places, and vehicles the rest of the ERP refers to. Drivers and
          transport feed the Dispatch module; warehouses feed everywhere.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-[200px_minmax(0,1fr)]">
        <aside aria-label="ERP settings sections">
          <nav className="flex flex-row gap-1 overflow-x-auto md:flex-col">
            {NAV_ENTRIES.map((entry) => {
              const Icon = entry.icon;
              return (
                <Link
                  key={entry.to}
                  to={entry.to}
                  className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm opacity-80 transition-colors hover:bg-black/5 hover:opacity-100 [&.active]:bg-[var(--accent)] [&.active]:font-medium [&.active]:text-white [&.active]:opacity-100 dark:hover:bg-white/5"
                  activeProps={{ className: "active" }}
                  activeOptions={{ exact: false }}
                >
                  <Icon className="size-4 opacity-70" aria-hidden />
                  <span>{entry.label}</span>
                </Link>
              );
            })}
          </nav>
        </aside>
        <section className="min-w-0">
          <Outlet />
        </section>
      </div>
    </div>
  );
}
